import { filterQuery } from './historyApi';
import { del, getJson, postJson } from './restClient';
import type {
  AnalysisHistoryFilters,
  ForkCompareResponse,
  ForkListResponse,
  ForkPreview,
  ForkRequest,
  ForkResponse,
  ForkRunState,
} from 'types/history';

/**
 * Forking a stored analysis onto another engine.
 *
 * A fork replays a past run's *input data set* through a different engine, so the
 * engine is the only variable — comparing two engines by running them at two
 * different moments compares two different markets instead.
 *
 * The one thing to know from the client side: a fork is a real Claude call. On
 * `brainer_v1` that is Opus 5, roughly $0.50 and several minutes each, which is
 * why single forks get a long timeout and batches go through preview → confirm →
 * background sweep rather than firing on click.
 */

/** A single fork can be a multi-minute Opus 5 call. */
const FORK_TIMEOUT_MS = 600_000;

/**
 * One analysis and the forks replayed from its input.
 *
 * Carries each run's identity plus a `preview` of what it proposed — but no
 * verdicts. Those come from `getStoredBacktestsByIds` for all the returned ids at
 * once, because that call is already keyed by the rule set they were scored under,
 * and its rollups already pair each proposal's levels with its outcome.
 */
export function listForks(
	analysisId: string,
	signal?: AbortSignal,
): Promise<ForkListResponse> {
	return getJson<ForkListResponse>(`/analyses/${analysisId}/forks`, signal);
}

/** Replay one analysis through another engine. Resolves when the run finishes. */
export function forkAnalysis(
  analysisId: string,
  body: ForkRequest,
  signal?: AbortSignal,
): Promise<ForkResponse> {
  return postJson<ForkResponse>(
    `/analyses/${analysisId}/fork`,
    body,
    signal,
    FORK_TIMEOUT_MS,
  );
}

/**
 * Filters that scope a batch fork to a set of parents.
 *
 * Deliberately a subset of the History filters: verdict/grade/side/trend describe
 * a run's *output*, and selecting parents by the answer their old engine gave
 * would hand the new engine a set pre-filtered on the thing being measured.
 */
export type ForkSweepFilters = Pick<
  AnalysisHistoryFilters,
  'asset' | 'engine' | 'exchange' | 'market' | 'timeframe' | 'since' | 'until'
>;

function sweepQuery(filters: ForkSweepFilters): string {
  const qs = filterQuery(filters);
  return qs.toString() ? `?${qs.toString()}` : '';
}

export interface ForkSweepBody extends ForkRequest {
  /** Max parents to fork, newest first. Hard-capped server-side. */
  limit: number;
}

/**
 * What a batch fork would do, before any money is spent: how many rows are
 * eligible, why the rest are not, and the likely cost. Always call this before
 * `startForkSweep` — the confirm dialog exists because a 200-row v1 sweep is
 * three figures.
 */
export function previewForkSweep(
  body: ForkSweepBody,
  filters: ForkSweepFilters = {},
  signal?: AbortSignal,
): Promise<ForkPreview> {
  return postJson<ForkPreview>(`/analyses/fork/preview${sweepQuery(filters)}`, body, signal);
}

/** Start the background sweep. Returns immediately with its initial state. */
export function startForkSweep(
  body: ForkSweepBody,
  filters: ForkSweepFilters = {},
  signal?: AbortSignal,
): Promise<ForkRunState> {
  return postJson<ForkRunState>(`/analyses/fork/run${sweepQuery(filters)}`, body, signal);
}

/** Progress of the running sweep, or the last one's terminal state. */
export function getForkSweepState(signal?: AbortSignal): Promise<ForkRunState> {
  return getJson<ForkRunState>('/analyses/fork/run', signal);
}

/**
 * Ask the sweep to stop. Cooperative — forks already in flight are billed either
 * way, so they land rather than being thrown away.
 */
export function cancelForkSweep(signal?: AbortSignal): Promise<ForkRunState> {
  return del<ForkRunState>('/analyses/fork/run', signal);
}

/**
 * Parent scoping for a comparison. Note the absence of `engine`: on this endpoint
 * `engine` names the *fork* engines being compared, so letting a parent-engine
 * filter share the key would make the query mean two things at once.
 */
export type ForkCompareFilters = Omit<ForkSweepFilters, 'engine'>;

export interface ForkCompareParams extends ForkCompareFilters {
  /** Engines to compare, as they appear in the audit log (a `_nohm` control is a
   *  separate engine here, exactly as History treats it). */
  engines: string[];
  window?: number;
  closeWindow?: number;
  includeBaseline?: boolean;
}

/**
 * Head-to-head engine comparison over one set of inputs. The server intersects
 * the parent sets, so every engine is judged on identical data.
 */
export function compareForks(
  params: ForkCompareParams,
  signal?: AbortSignal,
): Promise<ForkCompareResponse> {
  const { engines, window, closeWindow, includeBaseline, ...filters } = params;
  const qs = filterQuery(filters);
  for (const engine of engines) qs.append('engine', engine);
  if (window != null) qs.set('window', String(window));
  // 0 is meaningful (time stop off), so compare against null rather than falsy.
  if (closeWindow != null) qs.set('close_window', String(closeWindow));
  if (includeBaseline != null) qs.set('include_baseline', String(includeBaseline));
  return getJson<ForkCompareResponse>(`/analyses/fork/compare?${qs.toString()}`, signal);
}
